import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Axios from 'axios';
import { FormattedMessage, injectIntl } from 'react-intl';
import classnames from 'classnames';
import { Chart, BarElement, Legend, LinearScale, Tooltip, CategoryScale } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import intlShape from '@/shared/utils/intlShape';

import LoadingAnimation from '@/shared/components/LoadingAnimation';
import api from '@/shared/api';
import config from '@/config';
import handleHttpError from '@/shared/services/HandleHttpError';
import { currencyTooltipLabelWithInlt } from '@/shared/utils/chartjsHelpers';

import { injectStateService, StateService } from '@/shared/services/StateService';
import messages from '../dashboardMessages';

Chart.register(Legend, LinearScale, Tooltip, CategoryScale, BarElement);
const getDataSetColor = datasetIndex => (datasetIndex % 2 === 0 ? '#E0DBFD' : '#7564B6');
const getLabelColors = labels => labels.map((item, i) => (i % 2 === 0 ? '#E0DBFD' : '#7564B6'));

const TopCustomerRevenueDashboard = ({ intl, companyId, stateService }) => {
  const [showComparison, setShowComparison] = useState(false);
  const [rawData, setRawData] = useState(null);
  const [chartData, setChartData] = useState(null);

  const buildSingleYear = useCallback(() => {
    if (!rawData || !rawData.datasets) return;
    const [currentYearDataset] = rawData.datasets;
    if (!currentYearDataset) return;

    const chartXLabels = rawData.xLabels;
    const year = currentYearDataset.metadata;
    setChartData({
      labels: rawData.xLabels,
      datasets: [
        {
          label: `${intl.formatMessage(messages.FiscalYear)} ${year}`,
          backgroundColor: getLabelColors(chartXLabels),
          borderColor: getLabelColors(chartXLabels),
          maxBarThickness: 100,
          data: currentYearDataset.values,
        },
      ],
    });
  }, [intl, rawData]);
  const buildMultiYear = useCallback(() => {
    if (!rawData || !rawData.datasets) return;
    const { datasets = [], xLabels } = rawData;
    setChartData({
      labels: xLabels,
      datasets: datasets.map((dataset, idx) => ({
        label: `${intl.formatMessage(messages.FiscalYear)} ${dataset.metadata}`,
        backgroundColor: getDataSetColor(idx),
        borderColor: getDataSetColor(idx),
        maxBarThickness: 100,
        data: dataset.values,
      })),
    });
  }, [intl, rawData]);

  const toggleMode = () => {
    setShowComparison(!showComparison);
  };

  useEffect(() => {
    setRawData(null);
    setChartData(null);
    const cancelToken = Axios.CancelToken.source();

    api.base
      .get(`${config.appUrls.api}/api/Invoice/TopCustomerRevenue/`, {
        params: {
          companyId,
        },
        cancelToken: cancelToken.token,
      })
      .then(({ data }) => {
        setRawData(data || {});
      }, handleHttpError(intl));
    return () => {
      if (cancelToken) cancelToken.cancel('Operation canceled due to new request.');
    };
  }, [companyId, intl]);
  useEffect(() => {
    if (showComparison) {
      buildMultiYear();
    } else {
      buildSingleYear();
    }
  }, [rawData, showComparison, buildMultiYear, buildSingleYear]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: { display: showComparison },
        ticks: {
          callback(idx) {
            const scale = this;
            const value = scale.getLabelForValue(idx);
            if (value.length > 5) {
              const substring = value.substr(0, 5);
              return `${substring}..`;
            }
            return value;
          },
        },
      },
      y: {
        ticks: {
          beginAtZero: true,
          callback: (value = 0) =>
            intl.formatNumber(value, {
              format: 'currency',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
        },
      },
    },
    interaction: {
      mode: 'index',
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        xPadding: 10,
        yPadding: 10,
        displayColors: false,
        callbacks: {
          label: context => currencyTooltipLabelWithInlt(context, intl),
        },
      },
    },
    events: ['click', 'mousemove'], // this is needed, otherwise onHover is not fired
    onHover: (event, chartElement) => {
      // eslint-disable-next-line no-param-reassign
      event.native.target.style.cursor = chartElement[0] ? 'pointer' : 'default';
    },
    onClick: (event, chartElements) => {
      const [element] = chartElements || [];
      if (!element) return;
      // eslint-disable-next-line no-underscore-dangle
      const dataset = rawData.datasets[element.datasetIndex];
      // eslint-disable-next-line no-underscore-dangle
      const relationId = dataset.linkIds[element.index];
      if (!relationId) return;
      stateService.gotoRelation(relationId);
    },
  };

  const loading = !rawData;
  const hasData = !!(chartData && chartData.datasets && chartData.datasets.length);

  return (
    <div className="dashboard-chart">
      <div className="title-block">
        <FormattedMessage
          {...(showComparison
            ? messages.MostValuableCustomersComparison
            : messages.MostValuableCustomers)}
        />
        {!!rawData && (
          <a
            className="right"
            title={intl.formatMessage(
              showComparison
                ? messages.SwitchToCurrentFiscalYear
                : messages.SwitchToComparisonFiscalYears,
            )}
            onClick={() => toggleMode()}
          >
            <i className="fa fa-bar-chart" />
          </a>
        )}
      </div>
      <div
        className={classnames('content-block nothing-to-bill', { nodata: !loading && !hasData })}
      >
        {loading && <LoadingAnimation />}
        {!loading && hasData && (
          <div className="chart-container">
            <Bar data={chartData} options={chartOptions} />
          </div>
        )}
      </div>
    </div>
  );
};

TopCustomerRevenueDashboard.propTypes = {
  intl: intlShape.isRequired,
  companyId: PropTypes.string.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
};

export default injectStateService(injectIntl(TopCustomerRevenueDashboard));
